import React, { useState, useEffect, useRef } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import { getSolutionHowToPoints, getSolutionMappedTo, getSolutionFromProblemMappedTo } from '../../../../functions/get_solution_to';
import { getSolutionFromProblem } from '../../../../functions/solution';
import { getProblemImages } from '../../../../functions/get_problem';
import { convertDay } from '../../../../functions/date_converter';
import FileReaderWidget from '../../../../widgets/file_reader/file_reader';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import '../../../report_screen/report_screen.css';

export default function WizardReportForm({ eventData, problemData, solutionData, solutionImplementationData, onDataChange, initialData }) {
    const [currentDomain, setCurrentDomain] = useState(eventData?.eventType || 'Theory');
    const [mapCenter, setMapCenter] = useState({
        lat: 20.5937,
        lng: 78.9629
    });
    const [mapLoaded, setMapLoaded] = useState(false);
    const mapRef = useRef(null);
    const [problemImages, setProblemImages] = useState([]);
    const [solutionImages, setSolutionImages] = useState([]);
    const [solutionImplementationImages, setSolutionImplementationImages] = useState([]);
    const problemImagesSliderRef = useRef();
    const solutionImagesSliderRef = useRef();
    const solutionImplementationSliderRef = useRef();

    // Update domain when eventData changes
    useEffect(() => {
        if (eventData?.eventType) {
            setCurrentDomain(eventData.eventType);
        }
    }, [eventData?.eventType]);

    // Update eventData when domain changes
    const handleDomainChange = (domain) => {
        setCurrentDomain(domain);
        if (onDataChange && eventData) {
            onDataChange({
                ...eventData,
                eventType: domain
            });
        }
    };

    useEffect(() => {
        const geocodeLocation = async () => {
            if (eventData?.location && eventData.location.trim() !== '') {
                try {
                    const geocodeURL = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(eventData.location)}&key=AIzaSyA1fQ3PPqStI2hluImIPswrqIFYcSobIZI`;
                    const response = await fetch(geocodeURL);
                    const data = await response.json();

                    if (data.status === 'OK' && data.results && data.results.length > 0) {
                        const location = data.results[0].geometry.location;
                        setMapCenter({
                            lat: location.lat,
                            lng: location.lng
                        });
                    }
                } catch (error) {
                    console.error('Error geocoding location:', error);
                }
            }
        };

        if (eventData?.location) {
            geocodeLocation();
        }
    }, [eventData?.location]);

    useEffect(() => {
        if (mapRef.current && mapLoaded && mapCenter.lat !== 20.5937 && mapCenter.lng !== 78.9629) {
            try {
                mapRef.current.setCenter(mapCenter);
                mapRef.current.setZoom(12);
            } catch (error) {
                console.error('Error updating map center:', error);
            }
        }
    }, [mapCenter, mapLoaded]);

    // Format date - handle both MM/DD/YYYY format and ISO timestamp
    const eventDate = eventData?.eventDate || '';
    let formattedDate = '';
    if (eventDate) {
        // Check if it's already in MM/DD/YYYY format
        if (eventDate.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
            formattedDate = eventDate;
        } else {
            // Try to convert from timestamp
            try {
                formattedDate = convertDay(eventDate);
            } catch (e) {
                formattedDate = eventDate;
            }
        }
    }

    // Determine file types
    const youtubeLink = eventData?.youtubeLink || null;
    const isYouTubeVideo = youtubeLink && (youtubeLink.includes('youtube.com') || youtubeLink.includes('youtu.be'));

    const hasUploadedFile = eventData?.file && eventData?.uploadedFileName;

    // Convert base64 file to data URL for display
    const getFileDataUrl = () => {
        if (eventData?.file) {
            // File is stored as base64, convert to data URL
            const fileExtension = eventData.uploadedFileName?.split('.').pop()?.toLowerCase();
            if (fileExtension === 'pdf') {
                return `data:application/pdf;base64,${eventData.file}`;
            } else if (['jpg', 'jpeg', 'png'].includes(fileExtension)) {
                return `data:image/${fileExtension === 'jpg' ? 'jpeg' : fileExtension};base64,${eventData.file}`;
            }
        }
        return null;
    };

    const fileDataUrl = getFileDataUrl();
    const fileType = eventData?.uploadedFileName?.endsWith('.pdf') ? 'pdf' :
        eventData?.uploadedFileName?.match(/\.(jpg|jpeg|png)$/i) ? 'image' : null;

    const mapContainerStyle = {
        width: '100%',
        height: '300px',
        borderRadius: '5px',
        position: 'relative'
    };

    const domain = currentDomain;

    // Get solution mapped to
    const solutionMappedTo = solutionData?.solutionMappedTo || [];
    if (problemData?.pickedPMT && !solutionMappedTo.length) {
        // Calculate if not provided
        const solutions = getSolutionMappedTo(problemData.pickedPMT, domain);
        if (solutions.length > 0) {
            solutionMappedTo.push(...solutions);
        }
    }

    // Get solution how to points
    const solutionHowToPoints = [];
    if (problemData?.pickedPMT) {
        const points = getSolutionHowToPoints(problemData.pickedPMT, domain);
        solutionHowToPoints.push(...points);
    }

    // Get images for carousels
    useEffect(() => {
        if (problemData?.pickedPMT && problemData.pickedPMT.length > 0 && domain) {
            // Get problem images
            const problemImgs = getProblemImages(problemData.pickedPMT, domain);
            setProblemImages(problemImgs);

            // Get solution images
            const solutionImgs = getSolutionFromProblem(problemData.pickedPMT, domain);
            setSolutionImages(solutionImgs);

            // Get solution implementation images
            const solutionImplImgs = getSolutionFromProblemMappedTo(problemData.pickedPMT, domain);
            setSolutionImplementationImages(solutionImplImgs);
        } else {
            setProblemImages([]);
            setSolutionImages([]);
            setSolutionImplementationImages([]);
        }
    }, [problemData?.pickedPMT, domain]);

    const settingsSlick = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
    };

    return (
        <div className="wizard-report-form">
            <div className="report-content-container">
                {/* Left Column - Text Content */}
                <div className="report-left-column">
                    {/* About Event Section */}
                    <div className="section-container">
                        <div className="domain-pills">
                            <button
                                className={`domain-pill ${currentDomain === 'Theory' ? 'active' : ''}`}
                                onClick={() => handleDomainChange('Theory')}
                            >
                                Theory Domain
                            </button>
                            <button
                                className={`domain-pill ${currentDomain === 'Communication' ? 'active' : ''}`}
                                onClick={() => handleDomainChange('Communication')}
                            >
                                Communication Domain
                            </button>
                        </div>
                        <h2 className="section-title">About Event</h2>

                        <div className="detail-row">
                            <span className="detail-label">Event Title</span>
                            <span className="detail-value">{eventData?.eventName || ''}</span>
                        </div>

                        <div className="detail-row">
                            <span className="detail-label">Event Location</span>
                            <span className="detail-value">{eventData?.location || ''}</span>
                        </div>

                        <div className="detail-row">
                            <span className="detail-label">Event Date</span>
                            <span className="detail-value">{formattedDate}</span>
                        </div>

                        <div className="detail-row-full">
                            <span className="detail-label">Event Description</span>
                            <div className="detail-value description-text" dangerouslySetInnerHTML={{ __html: eventData?.eventDesc || '' }}></div>
                        </div>

                        <div className="detail-row">
                            <span className="detail-label">Problem Identified from Event</span>
                            <span className="detail-value negative">{eventData?.problem || ''}</span>
                        </div>

                        <div className="detail-row">
                            <span className="detail-label">Negative Function Executed</span>
                            <span className="detail-value negative">{eventData?.negative || ''}</span>
                        </div>
                    </div>

                    {/* About Problem Section */}
                    <div className="section-container">
                        <h2 className="section-title">About Problem</h2>

                        <div className="detail-row-full">
                            <span className="detail-label">Problem Mapped To:</span>
                            {problemData?.pickedPMT && problemData.pickedPMT.length > 0 ? (
                                <div className="checklist-items">
                                    {problemData.pickedPMT.map((item, index) => (
                                        <div key={index} className="checklist-item">
                                            <CheckIcon className="check-icon" />
                                            <span className="checklist-text negative">{item}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                        </div>

                        <div className="detail-row-full">
                            <span className="detail-label">Principle Deficiency</span>
                            {problemData?.pickedPD && problemData.pickedPD.length > 0 ? (
                                <div className="checklist-items">
                                    {problemData.pickedPD.map((item, index) => (
                                        <div key={index} className="checklist-item">
                                            <CheckIcon className="check-icon" />
                                            <span className="checklist-text blue">{item}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    </div>

                    {/* About Solution Section */}
                    <div className="section-container">
                        <h2 className="section-title">About Solution</h2>
                        <div className="detail-row-full">
                            <span className="detail-label">Solution Mapped To</span>
                            {solutionMappedTo.length > 0 ? (
                                <div className="checklist-items">
                                    {solutionMappedTo.map((item, index) => (
                                        <div key={index} className="checklist-item">
                                            <CheckIcon className="check-icon" />
                                            <span className="checklist-text green">{item}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                        </div>

                        <div className="detail-row-full">
                            <span className="detail-label">Principle Needed</span>
                            {problemData?.pickedPD && problemData.pickedPD.length > 0 ? (
                                <div className="checklist-items">
                                    {problemData.pickedPD.map((item, index) => (
                                        <div key={index} className="checklist-item">
                                            <CheckIcon className="check-icon" />
                                            <span className="checklist-text blue">{item}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    </div>

                    {/* About Solution Implementation Section */}
                    <div className="section-container">
                        <h2 className="section-title">About Solution Implementation</h2>
                        <div className="detail-row-full">
                            <span className="detail-label">Solution How To</span>
                            {solutionHowToPoints.length > 0 ? (
                                <div className="checklist-items solution-how-to-items">
                                    {solutionHowToPoints.map((item, index) => {
                                        if (item === ' ') {
                                            return <br key={index} />;
                                        }
                                        return (
                                            <div key={index} className="checklist-item">
                                                <CheckIcon className="check-icon" />
                                                <span className="checklist-text green">{item}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>

                {/* Right Column - Visual/Media Content */}
                <div className="report-right-column">
                    {/* Event Map Section */}
                    <div className="media-section">
                        <h3 className="media-title">Event Map</h3>
                        <div className="media-content map-container" style={{ width: '100%', height: '300px', position: 'relative', backgroundColor: '#f5f5f5', border: '1px solid #ddd' }}>
                            {eventData?.location && eventData.location.trim() !== '' ? (
                                <div style={{ width: '100%', height: '300px', position: 'relative' }}>
                                    <LoadScript
                                        googleMapsApiKey="AIzaSyA1fQ3PPqStI2hluImIPswrqIFYcSobIZI"
                                        loadingElement={<div style={{ width: '100%', height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>Loading map...</div>}
                                    >
                                        <GoogleMap
                                            key={`map-${mapCenter.lat}-${mapCenter.lng}`}
                                            mapContainerStyle={mapContainerStyle}
                                            zoom={mapCenter.lat !== 20.5937 && mapCenter.lng !== 78.9629 ? 12 : 2}
                                            center={mapCenter}
                                            options={{
                                                disableDefaultUI: false,
                                                zoomControl: true,
                                                streetViewControl: false,
                                                mapTypeControl: false,
                                                fullscreenControl: false,
                                            }}
                                            onLoad={(map) => {
                                                mapRef.current = map;
                                                setMapLoaded(true);
                                                if (mapCenter.lat !== 20.5937 && mapCenter.lng !== 78.9629) {
                                                    setTimeout(() => {
                                                        map.setCenter(mapCenter);
                                                        map.setZoom(12);
                                                    }, 100);
                                                }
                                            }}
                                        >
                                            {mapCenter.lat !== 20.5937 && mapCenter.lng !== 78.9629 && (
                                                <Marker
                                                    position={mapCenter}
                                                    title={eventData?.location}
                                                />
                                            )}
                                        </GoogleMap>
                                    </LoadScript>
                                </div>
                            ) : (
                                <div style={{ width: '100%', height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
                                    No location set for this event
                                </div>
                            )}
                        </div>
                    </div>

                    {/* YouTube Video Section */}
                    {isYouTubeVideo && (
                        <div className="media-section">
                            <h3 className="media-title">YouTube Video</h3>
                            <div className="media-content video-container">
                                <FileReaderWidget file={youtubeLink} type="link" />
                            </div>
                        </div>
                    )}

                    {/* Files Section */}
                    {hasUploadedFile && fileDataUrl && fileType && (
                        <div className="media-section">
                            <h3 className="media-title">Files</h3>
                            <div className="media-content file-container">
                                {fileType === 'image' ? (
                                    <img src={fileDataUrl} alt="Uploaded file" style={{ maxWidth: '100%', maxHeight: '400px', objectFit: 'contain' }} />
                                ) : fileType === 'pdf' ? (
                                    <iframe
                                        src={fileDataUrl}
                                        style={{ width: '100%', height: '400px', border: 'none' }}
                                        title="PDF file"
                                    />
                                ) : null}
                            </div>
                        </div>
                    )}

                    {/* Problem Images Carousel */}
                    {problemImages.length > 0 && (
                        <div className="media-section">
                            <h3 className="media-title">Problem Mapped To</h3>
                            <div className="media-content">
                                <div className="report-carousel-container">
                                    <Slider ref={problemImagesSliderRef} className='report-carousel' {...settingsSlick}>
                                        {problemImages.map((image, index) => (
                                            <div key={index} className="report-carousel-slide">
                                                <img alt={`Problem Mapped To ${index + 1}`} src={image} className="report-carousel-image" />
                                            </div>
                                        ))}
                                    </Slider>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Solution Images Carousel */}
                    {solutionImages.length > 0 && (
                        <div className="media-section">
                            <h3 className="media-title">Solution Mapped To</h3>
                            <div className="media-content">
                                <div className="report-carousel-container">
                                    <Slider ref={solutionImagesSliderRef} className='report-carousel' {...settingsSlick}>
                                        {solutionImages.map((image, index) => (
                                            <div key={index} className="report-carousel-slide">
                                                <img alt={`Solution Mapped To ${index + 1}`} src={image} className="report-carousel-image" />
                                            </div>
                                        ))}
                                    </Slider>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Solution Implementation Images Carousel */}
                    {solutionImplementationImages.length > 0 && (
                        <div className="media-section">
                            <h3 className="media-title">Solution How To</h3>
                            <div className="media-content">
                                <div className="report-carousel-container">
                                    <Slider ref={solutionImplementationSliderRef} className='report-carousel' {...settingsSlick}>
                                        {solutionImplementationImages.map((image, index) => (
                                            <div key={index} className="report-carousel-slide">
                                                <img alt={`Solution How To ${index + 1}`} src={image} className="report-carousel-image" />
                                            </div>
                                        ))}
                                    </Slider>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// Export validation function (report step is read-only, so always valid)
export const validateReportForm = () => {
    return { valid: true };
};
